import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { API_ORIGIN } from '../services/api';

/**
 * Subscribe to real-time order events for a restaurant (new_order, order_status_updated,
 * and the POS pos_payment_updated event). Consumers that don't care about a given type
 * simply ignore it.
 * @param {string|null} restaurantId - Restaurant ID
 * @param {(payload: { type: 'new'|'status'|'payment'|'reconnect_failed', order?: object, data?: object }) => void|null} onUpdate - Callback for events (null to disable)
 * @param {boolean} [enabled=true] - When false, socket is not connected (e.g. when viewing history tab)
 */
const useOrdersSocket = (restaurantId, onUpdate, enabled = true) => {
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  useEffect(() => {
    if (!restaurantId || !enabled) return;

    const socket = io(API_ORIGIN, {
      transports: ['websocket'],
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      withCredentials: true, // send the httpOnly auth cookie on the handshake
    });

    socket.on('connect', () => {
      socket.emit('join_restaurant', restaurantId);
    });

    socket.on('connect_error', (err) => {
      console.warn('Orders socket connection rejected:', err.message);
    });

    // Surface transport/engine errors instead of swallowing them silently.
    socket.on('error', (err) => {
      console.error('Orders socket error:', err && err.message ? err.message : err);
    });

    // After reconnectionAttempts is exhausted the client stops retrying and goes
    // silent — log it and signal the page so it can fall back to a manual refresh.
    // The listener lives on the Manager (socket.io), which can be reused across
    // remounts, so it is removed explicitly in cleanup below.
    const handleReconnectFailed = () => {
      console.error('Orders socket gave up reconnecting');
      if (onUpdateRef.current) onUpdateRef.current({ type: 'reconnect_failed' });
    };
    socket.io.on('reconnect_failed', handleReconnectFailed);

    socket.on('new_order', (order) => {
      if (onUpdateRef.current) onUpdateRef.current({ type: 'new', order });
    });

    socket.on('order_status_updated', (data) => {
      if (onUpdateRef.current) onUpdateRef.current({ type: 'status', data });
    });

    // POS: a card payment's status changed (webhook-driven). Lets the POS settle a
    // tab on the realtime event instead of polling the payment row.
    socket.on('pos_payment_updated', (data) => {
      if (onUpdateRef.current) onUpdateRef.current({ type: 'payment', data });
    });

    socket.on('disconnect', () => {});

    return () => {
      socket.io.off('reconnect_failed', handleReconnectFailed);
      socket.disconnect();
    };
  }, [restaurantId, enabled]);
};

export default useOrdersSocket;
